// QC issue lifecycle contract for the workspace, matching the qc app's wire shapes (camelCased by DRF).

export type QcIssueStatus =
  | 'first_pass'
  | 'pending_assessor_action'
  | 'ready_for_tpn_review'
  | 'approved'
  | 'needs_update';

export type QcIssueCategory =
  | 'insufficient_detail'
  | 'missing_evidence'
  | 'needs_clarification'
  | 'incorrect_answer'
  | 'other';

export const QC_ISSUE_STATUS_LABELS: Record<QcIssueStatus, string> = {
  first_pass: 'First Pass',
  pending_assessor_action: 'Pending Assessor Action',
  ready_for_tpn_review: 'Ready for TPN Review',
  approved: 'Approved',
  needs_update: 'Needs Update',
};

export const QC_ISSUE_CATEGORY_LABELS: Record<QcIssueCategory, string> = {
  insufficient_detail: 'Insufficient Detail',
  missing_evidence: 'Missing Evidence',
  needs_clarification: 'Needs Clarification',
  incorrect_answer: 'Incorrect Answer',
  other: 'Other',
};

// Composer order (prototype qcTypeOptions) — the four review categories, then Other.
export const QC_ISSUE_CATEGORY_ORDER: QcIssueCategory[] = [
  'missing_evidence',
  'insufficient_detail',
  'incorrect_answer',
  'needs_clarification',
  'other',
];

export interface QcIssueAuthor {
  name: string;
  role: 'assessor' | 'tpn_admin';
  initials: string;
}

export interface QcIssueReply {
  id: number;
  author: QcIssueAuthor;
  text: string;
  createdAt: string;
}

export interface QcIssue {
  id: number;
  question: number;
  status: QcIssueStatus;
  category: QcIssueCategory;
  text: string;
  author: QcIssueAuthor;
  createdAt: string;
  acceptedFromAi?: boolean;
  provenance?: string;
  statusBy?: string;
  statusAt?: string;
  certainty?: number;
  replies: QcIssueReply[];
  // QC Recheck's advisory second look — admin-only, present only while awaiting the admin's call; recommendation null = degraded run, comment still shown.
  firstPassRecommendation?: {
    recommendation: 'approve' | 'needs_update' | null;
    comment: string;
    roundNumber: number;
    certainty: number | null;
  } | null;
}

export type QcIssuesByQuestionId = Record<number, QcIssue[]>;

// General per-question thread between assessor and TPN Admin — not tied to an issue, never a status change.
export interface QcComment {
  id: number;
  author: QcIssueAuthor;
  text: string;
  createdAt: string;
}

export type QcCommentsByQuestionId = Record<number, QcComment[]>;

// GET /qc/workflows/<id>/comments/ — flat rows carrying the question id for client-side grouping.
export interface WorkflowQcComment extends QcComment {
  question: number;
}

// GET /qc/workflows/<id>/review-cycle/ (null before any cycle); qcReviewSubmitted derives from submittedAt.
export interface QcReviewCycleState {
  id: number;
  roundNumber: number;
  // Set while the round is with TPN (all Ready → admin resolving); null while the assessor's half is open.
  roundClosedAt?: string | null;
  submittedAt: string | null;
  approvedAt: string | null;
}

// First Pass rollup for a question with NO draft issue, always "No issue raised": agree carries the reasoning, not_evaluated renders plain copy; admin-only.
export interface QcFirstPassNote {
  reviewId: number;
  verdict: 'agree' | 'not_evaluated';
  rationale: string;
  confidence?: number;
  suspectedInjection: boolean;
  adminResponse?: 'accept' | 'edit' | 'reject';
  adminResponseNote?: string;
  // Observation channels: mentioned to the admin, never issues.
  spellingNotes?: string[];
  crossReferences?: string[];
}

// One row of GET /qc/issues/ (QCIssueAdminRowSerializer); roundNumber counts completed rechecks + the current round.
export interface AdminQcIssueRow {
  id: number;
  workflow: number;
  assessment: number | null;
  survey: number | null;
  vendor: string | null;
  siteOrApplication: string | null;
  question: number;
  questionShortCode: string | null;
  questionIsBestPractice: boolean;
  category: QcIssueCategory;
  text: string;
  status: QcIssueStatus;
  createdAt: string;
  approvedAt: string | null;
  roundNumber: number;
}

// GET /qc/workflows/<id>/runs/ — one summary per run; recheck runs carry reevaluations, not questions.
export interface AdminQcRunQuestion {
  questionId: number;
  shortCode: string | null;
  verdict: string;
  confidence: number | null;
  errorClass: string | null;
  suspectedInjection: boolean;
  spellingNoteCount: number;
  crossReferenceCount: number;
}

// One recheck outcome row (recommendation null = degraded; the comment names why).
export interface AdminQcRunReevaluation {
  issueId: number;
  questionId: number;
  shortCode: string | null;
  roundNumber: number;
  recommendation: 'approve' | 'needs_update' | null;
  certainty: number | null;
  comment: string;
}

export interface AdminQcRunSummary {
  id: number;
  reviewType: string;
  mode: string;
  status: string;
  modelVersion: string;
  promptVersion: string;
  guidanceVersion: number | null;
  triggeredBy: string;
  createdAt: string;
  updatedAt: string;
  dispatchedCount: number;
  completedCount: number;
  flaggedCount: number;
  escalatedCount: number;
  draftsCreated: number;
  draftsAccepted: number;
  draftsRejected: number;
  draftsPending: number;
  avgDraftRating: number | null;
  questions: AdminQcRunQuestion[];
  reevaluations: AdminQcRunReevaluation[];
}

// QC preview endpoints — a dry run reporting what a real First Pass would create, writing nothing.
export interface QcPreviewQuestionRef {
  assessorAnswerId: number;
  questionId: number;
  shortCode: string;
  title: string;
}

export interface QcPreviewScope {
  modelId: string;
  promptVersion: string;
  guidanceVersion: number | null;
  questions: QcPreviewQuestionRef[];
}

export interface QcPreviewRollup {
  verdict: string;
  errorClass: string | null;
  confidence: number | null;
  rationale: string;
  issueText: string;
  spellingNotes: string[];
  crossReferences: string[];
  suspectedInjection: boolean;
}

export interface QcPreviewSubControlVerdict {
  assessorQuestionAnswerId: number;
  verdict: string;
  errorClass: string | null;
  confidence: number | null;
  rationale: string;
  suspectedInjection: boolean;
}

// The QCIssue a production run would have written — null when nothing would be created.
export interface QcPreviewDraft {
  category: QcIssueCategory;
  text: string;
  certainty: number | null;
}

export interface QcPreviewResult extends QcPreviewQuestionRef {
  modelId: string;
  promptVersion: string;
  guidanceVersion: number | null;
  rollup: QcPreviewRollup;
  subControlVerdicts: QcPreviewSubControlVerdict[];
  wouldCreateIssue: boolean;
  wouldCreate: QcPreviewDraft | null;
}
